"use client";

import { useState } from "react";
import { addDoc, collection } from "firebase/firestore";
import { db } from "@/lib/firebase";
import { useHouseholdContext } from "@/contexts/HouseholdContext";
import { useTranslations } from "@/contexts/I18nContext";
import {
  buildCreateChoreDoc,
  CHORE_CATEGORIES,
  CHORE_RECURRENCE_OPTIONS,
  type ChoreCategoryValue,
  type ChoreRecurrenceValue,
} from "@/lib/createChore";

interface CreateChoreModalProps {
  onClose: () => void;
  /** Pre-fill values when reopening from an Edit action (deferred — placeholder). */
  initialTitle?: string;
}

/**
 * Modal "Create chore" flow capturing every iOS-canonical field
 * (`Views/Chores/ChoresView.swift:328-637` `CreateChoreSheet`).
 * Replaces the previous one-line title-only quick-add that wrote
 * crippled chore docs.
 */
export function CreateChoreModal({
  onClose,
  initialTitle = "",
}: CreateChoreModalProps) {
  const { householdId, profile } = useHouseholdContext();
  const t = useTranslations();

  const [title, setTitle] = useState(initialTitle);
  const [description, setDescription] = useState("");
  const [category, setCategory] = useState<ChoreCategoryValue>("general");
  const [recurrence, setRecurrence] = useState<ChoreRecurrenceValue>("Weekly");
  const [pointsStr, setPointsStr] = useState("");
  const [dueDate, setDueDate] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!householdId || !profile?.uid) return;

    const points = pointsStr.trim() ? Number(pointsStr) : undefined;
    const doc = buildCreateChoreDoc({
      title,
      description,
      category,
      recurrenceType: recurrence,
      points: Number.isFinite(points) ? points : undefined,
      dueDate: dueDate || undefined,
      householdId,
      createdBy: profile.uid,
    });
    if (!doc) {
      setError(t("chores.create.errorMissingTitle"));
      return;
    }

    setSubmitting(true);
    setError(null);
    try {
      const choresRef = collection(db, `households/${householdId}/chores`);
      await addDoc(choresRef, doc);
      onClose();
    } catch (err) {
      console.error("Failed to create chore:", err);
      setError(t("chores.create.errorGeneric"));
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4">
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={onClose}
      />
      <div className="relative w-full sm:max-w-md bg-white dark:bg-gray-900 rounded-t-2xl sm:rounded-2xl shadow-xl overflow-hidden text-gray-900 dark:text-gray-100 max-h-[92vh] overflow-y-auto">
        <div className="flex items-center justify-between p-4 border-b border-gray-100 dark:border-white/10">
          <button
            type="button"
            onClick={onClose}
            className="text-blue-500 text-sm font-medium"
          >
            {t("common.cancel")}
          </button>
          <h2 className="font-bold text-base">{t("chores.create.title")}</h2>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={!title.trim() || submitting}
            className="text-sm font-semibold text-[color:var(--accent-color)] disabled:opacity-50"
          >
            {submitting ? t("chores.create.saving") : t("chores.create.save")}
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          {/* Title */}
          <div>
            <label className="block text-xs font-semibold text-gray-500 mb-1">
              {t("chores.create.titleLabel")}
            </label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder={t("chores.quickAdd.placeholder")}
              autoFocus
              className="w-full px-3 py-2 rounded-lg bg-gray-100 dark:bg-white/10 outline-none focus:ring-2 focus:ring-[color:var(--accent-color)]"
            />
          </div>

          {/* Description */}
          <div>
            <label className="block text-xs font-semibold text-gray-500 mb-1">
              {t("chores.create.descriptionLabel")}
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder={t("chores.create.descriptionPlaceholder")}
              rows={2}
              className="w-full px-3 py-2 rounded-lg bg-gray-100 dark:bg-white/10 resize-none outline-none focus:ring-2 focus:ring-[color:var(--accent-color)]"
            />
          </div>

          {/* Category */}
          <div>
            <label className="block text-xs font-semibold text-gray-500 mb-1">
              {t("chores.create.categoryLabel")}
            </label>
            <div className="flex flex-wrap gap-1.5">
              {CHORE_CATEGORIES.map((cat) => {
                const active = category === cat;
                return (
                  <button
                    key={cat}
                    type="button"
                    onClick={() => setCategory(cat)}
                    className={`px-2.5 py-1 rounded-full text-xs font-medium transition-colors ${
                      active
                        ? "bg-[color:var(--accent-color)] text-white"
                        : "bg-gray-100 dark:bg-white/10 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-white/15"
                    }`}
                  >
                    {t(`chores.category.${cat}`)}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Recurrence */}
          <div>
            <label className="block text-xs font-semibold text-gray-500 mb-1">
              {t("chores.create.recurrenceLabel")}
            </label>
            <div className="flex flex-wrap gap-1.5">
              {CHORE_RECURRENCE_OPTIONS.map((rec) => {
                const active = recurrence === rec;
                return (
                  <button
                    key={rec}
                    type="button"
                    onClick={() => setRecurrence(rec)}
                    className={`px-2.5 py-1 rounded-full text-xs font-medium transition-colors ${
                      active
                        ? "bg-[color:var(--accent-color)] text-white"
                        : "bg-gray-100 dark:bg-white/10 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-white/15"
                    }`}
                  >
                    {t(`chores.recurrence.${rec.toLowerCase()}`)}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Points + Due date — side by side */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-semibold text-gray-500 mb-1">
                {t("chores.create.pointsLabel")}
              </label>
              <input
                type="number"
                value={pointsStr}
                onChange={(e) => setPointsStr(e.target.value)}
                placeholder="0"
                min="0"
                className="w-full px-3 py-2 rounded-lg bg-gray-100 dark:bg-white/10 outline-none focus:ring-2 focus:ring-[color:var(--accent-color)]"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-500 mb-1">
                {t("chores.create.dueDateLabel")}
              </label>
              <input
                type="date"
                value={dueDate}
                onChange={(e) => setDueDate(e.target.value)}
                className="w-full px-3 py-2 rounded-lg bg-gray-100 dark:bg-white/10 outline-none focus:ring-2 focus:ring-[color:var(--accent-color)]"
              />
            </div>
          </div>

          {error && (
            <p className="text-red-500 text-sm">{error}</p>
          )}
        </form>
      </div>
    </div>
  );
}
